import Dexie from 'dexie'
import browser from 'webextension-polyfill'

import {
  DEFAULT_NETWORK,
  LUKSO_MAINNET_ID,
  LUKSO_TESTNET_ID,
} from '@/shared/NetworkUtils'
import getUniqueId from '@/shared/getUniqueId'
import {
  NetworkInfoDBSchema,
  RelayServiceDBSchema,
} from '@/shared/Database/UPSettings/models'

const ETHEREUM_SEPOLIA_HTTPS_RPC = 'https://ethereum-sepolia-rpc.publicnode.com'
const ETHEREUM_SEPOLIA_WS_RPC = 'wss://ethereum-sepolia-rpc.publicnode.com'

type DexieTable<T, K> = Dexie.Table<T, K>

export type DexieModelsGlobal = {
  networks: NetworkInfoDBSchema
  relayers: RelayServiceDBSchema
}

type RequiredNetworkInfo = Required<NetworkInfoDBSchema>

const networks: RequiredNetworkInfo[] = [
  {
    name: 'LUKSO Testnet',
    http: {
      url: 'https://rpc.testnet.lukso.network',
    },
    ws: {
      url: 'wss://ws-rpc.testnet.lukso.network',
    },
    relayer: {
      url: 'https://relayer.testnet.lukso.network/api',
    },
    explorer: {
      url: 'https://explorer.execution.testnet.lukso.network/tx/{transactionId}/internal-transactions',
    },
    isCustom: false,
    id: LUKSO_TESTNET_ID,
    chainId: '0x1069',
  },
  {
    name: 'LUKSO Mainnet',
    http: {
      url: 'https://rpc1.mainnet.lukso.dev/',
    },
    ws: {
      url: 'wss://ws-rpc.mainnet.lukso.network',
    },
    relayer: {
      url: 'https://relayer.mainnet.lukso.network/api',
    },
    explorer: {
      url: 'https://explorer.lukso.network/tx/{transactionId}?tab=internal',
    },
    isCustom: false,
    id: LUKSO_MAINNET_ID,
    chainId: '0x2a',
  },
  {
    name: 'Ethereum Mainnet',
    http: {
      url: 'https://ethereum-rpc.publicnode.com',
    },
    ws: {
      url: 'wss://ethereum-rpc.publicnode.com',
    },
    relayer: {
      url: undefined,
    },
    explorer: {
      url: 'https://etherscan.io/tx/{transactionId}',
    },
    isCustom: false,
    id: 'ethereum-mainnet',
    chainId: '0x1',
  },
  {
    name: 'Ethereum Sepolia',
    http: {
      url: ETHEREUM_SEPOLIA_HTTPS_RPC,
    },
    ws: {
      url: ETHEREUM_SEPOLIA_WS_RPC,
    },
    relayer: {
      url: undefined,
    },
    explorer: {
      url: 'https://sepolia.etherscan.io/tx/{transactionId}',
    },
    isCustom: false,
    id: 'ethereum-sepolia',
    chainId: '0xaa36a7',
  },
  {
    name: 'Base Mainnet',
    http: {
      url: 'https://base-rpc.publicnode.com/',
    },
    ws: {
      url: 'wss://base-rpc.publicnode.com',
    },
    relayer: {
      url: undefined,
    },
    explorer: {
      url: 'https://basescan.org/tx/{transactionId}',
    },
    isCustom: false,
    id: 'base-mainnet',
    chainId: '0x2105',
  },
  {
    name: 'Base Sepolia',
    http: {
      url: 'https://base-sepolia-rpc.publicnode.com/',
    },
    ws: {
      url: 'wss://base-sepolia-rpc.publicnode.com',
    },
    relayer: {
      url: undefined,
    },
    explorer: {
      url: 'https://sepolia.basescan.org/tx/{transactionId}',
    },
    isCustom: false,
    id: 'base-sepolia',
    chainId: '0x14a34',
  },
]

const relayers: RelayServiceDBSchema[] = [
  {
    id: getUniqueId(),
    name: 'Universal Relayer Testnet',
    apiUrl: networks[0].relayer.url!,
    chainIds: [networks[0].chainId],
    isCustom: false,
    createdAt: Date.now(),
  },
  {
    id: getUniqueId(),
    name: 'Universal Relayer Mainnet',
    apiUrl: networks[1].relayer.url!,
    chainIds: [networks[1].chainId],
    isCustom: false,
    createdAt: Date.now(),
  },
]

export class UPSettingsSchema extends Dexie {
  networks!: DexieTable<NetworkInfoDBSchema, string>
  relayers!: DexieTable<RelayServiceDBSchema, string>

  constructor(dbName: string) {
    super(dbName)

    this.version(1)
      .stores({
        networks: 'id, name, chainId',
        relayers: 'id, &name, *chainIds, *userIds, createdAt, &apiUrl',
      })
      .upgrade(async () => {
        await browser.storage.local.set({ networkId: DEFAULT_NETWORK })
      })
    this.version(2)
    this.version(3)
    this.version(4).upgrade(tx => {
      return tx
        .table('relayers')
        .toCollection()
        .modify(relayer => {
          if (relayer.name === 'LUKSO Relayer Mainnet') {
            relayer.name = 'UP Transaction Relay Service Mainnet'
          }
          if (relayer.name === 'LUKSO Relayer Testnet') {
            relayer.name = 'UP Transaction Relay Service Testnet'
          }
        })
    })
    this.version(5).upgrade(tx => {
      return tx
        .table('relayers')
        .toCollection()
        .modify(relayer => {
          if (relayer.name === 'UP Transaction Relay Service Mainnet') {
            relayer.name = 'Universal Relayer Mainnet'
          }
          if (relayer.name === 'UP Transaction Relay Service Testnet') {
            relayer.name = 'Universal Relayer Testnet'
          }
        })
    })
    this.version(6).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.http.url === 'https://rpc.testnet.lukso.network/') {
            network.http.url = 'https://rpc.testnet.lukso.gateway.fm/'
          }
        })
    })
    this.version(7).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.http.url === 'https://rpc.mainnet.lukso.network/') {
            network.http.url = 'https://rpc1.mainnet.lukso.dev/'
          }
        })
    })
    this.version(8).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.http.url === 'https://rpc.testnet.lukso.gateway.fm/') {
            network.http.url = 'https://rpc.testnet.lukso.network/'
          }
        })
    })
    this.version(9).upgrade(async tx => {
      await tx.table('networks').clear()
      await tx.table('networks').bulkAdd(networks)
    })
    this.version(10).upgrade(async tx => {
      await tx.table('networks').clear()
      await tx.table('networks').bulkAdd(networks)
    })
    this.version(11).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.http.url === 'https://rpc2.sepolia.org/') {
            network.http.url = ETHEREUM_SEPOLIA_HTTPS_RPC
          }
          if (network.ws.url === 'wss://rpc2.sepolia.org/') {
            network.ws.url = ETHEREUM_SEPOLIA_WS_RPC
          }
        })
    })
    this.version(12).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.ws.url === 'wss://rpc2.sepolia.org') {
            network.ws.url = ETHEREUM_SEPOLIA_WS_RPC
          }
        })
    })
    this.version(13).upgrade(async tx => {
      await tx.table('networks').clear()
      await tx.table('networks').bulkAdd(networks)
    })
    this.version(14).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.http.url === 'https://mainnet.infura.io/v3/') {
            network.http.url = 'https://ethereum-rpc.publicnode.com'
          }
          if (network.ws.url === 'wss://mainnet.infura.io/v3') {
            network.ws.url = 'wss://ethereum-rpc.publicnode.com'
          }
        })
    })
    this.version(15).upgrade(tx => {
      return tx
        .table('networks')
        .toCollection()
        .modify(network => {
          if (network.id === LUKSO_MAINNET_ID) {
            network.explorer['url'] =
              'https://explorer.lukso.network/tx/{transactionId}?tab=internal'
          }
        })
    })
    this.version(16).upgrade(async tx => {
      await tx.table('networks').clear()
      await tx.table('networks').bulkAdd(networks)
      await tx.table('relayers').clear()
      await tx.table('relayers').bulkAdd(relayers)
    })
    this.on('populate', () => {
      this.networks.bulkPut(networks)
      this.relayers.bulkPut(relayers)
    })
  }
}
